import { Snowflake } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { DeployFreezeWindow, LandingProjectConfig } from '@orgus/protocol';
import { Button, Dialog, Input, Segmented, cn, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { addFreeze } from './api.ts';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function localInput(ts: number): string {
  const d = new Date(ts - new Date(ts).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}

/** Stop deploys to one environment for a while: once (from, to) or every week. */
export function FreezeDialog({ env, config, onClose }: { env: string | null; config: LandingProjectConfig; onClose: () => void }) {
  const [kind, setKind] = useState<'once' | 'weekly'>('once');
  const [reason, setReason] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [days, setDays] = useState<number[]>([5]);
  const [start, setStart] = useState('16:00');
  const [end, setEnd] = useState('23:59');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  useEffect(() => {
    if (!env) return;
    const now = Date.now();
    setKind('once');
    setReason('');
    setFrom(localInput(now));
    setTo(localInput(now + 24 * 3600_000));
    setDays([5]);
    setError(null);
  }, [env]);

  const save = async () => {
    if (!env) return;
    setBusy(true);
    setError(null);
    try {
      const input: Omit<DeployFreezeWindow, 'id' | 'createdAt' | 'createdBy'> =
        kind === 'once' ? { reason, from: new Date(from).getTime(), to: new Date(to).getTime() } : { reason, weekly: { days, start, end, tz } };
      await addFreeze(config.projectId, env, input, config);
      toast.success(`${env} freeze added`, reason);
      onClose();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!env}
      onOpenChange={(v) => !v && !busy && onClose()}
      title={`Freeze deploys to ${env ?? ''}`}
      description="Deploys and promotes are refused and landings skip it; a rollback still runs."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={<Snowflake />} loading={busy} disabled={!reason.trim() || (kind === 'weekly' && !days.length)} onClick={save}>
            Add freeze
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Segmented
          label="Kind of window"
          value={kind}
          onChange={setKind}
          options={[
            { value: 'once', label: 'Once' },
            { value: 'weekly', label: 'Every week' },
          ]}
        />
        <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Launch weekend" hint="Shown to anyone who tries to deploy." />
        {kind === 'once' ? (
          <div className="grid grid-cols-2 gap-3">
            <Input label="From" type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label="To" type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        ) : (
          <>
            <fieldset>
              <legend className="mb-2 text-ui font-medium text-fg-muted">Days</legend>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d, i) => {
                  const on = days.includes(i);
                  return (
                    <Button
                      key={d}
                      size="sm"
                      variant="secondary"
                      aria-pressed={on}
                      onClick={() => setDays((cur) => (on ? cur.filter((x) => x !== i) : [...cur, i]))}
                      className={cn(on && 'border-accent bg-accent-soft text-fg hover:bg-accent-soft')}
                    >
                      {d}
                    </Button>
                  );
                })}
              </div>
            </fieldset>
            <div className="grid grid-cols-3 gap-3">
              <Input label="From" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
              <Input label="To" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
              <Input label="Time zone" value={tz} readOnly hint="This browser's zone." />
            </div>
          </>
        )}
        {error && (
          <p role="alert" className="text-sm text-failed">
            {error.what} {error.why} {error.next}
          </p>
        )}
      </div>
    </Dialog>
  );
}
